import React, { useState } from "react";
import { CheckCircle2, ChevronRight, BookOpen } from "lucide-react";
import metodoImg from "@/assets/metodo.jpeg";

export default function Methodology() {
  const [activePhase, setActivePhase] = useState(0);

  const phases = [
    {
      num: "01",
      title: "Fase 1 — Distacco Emotivo",
      subtitle: "Interrompere il legame emotivo tossico",
      description: "La prima fase è dedicata all'acquisizione di lucidità immediata. Attraverso strumenti pratici, guariamo l'astinenza affettiva e spezziamo quel filo invisibile che ti riporta costantemente indietro.",
      highlights: [
        "Uscire dalla *dipendenza affettiva* attiva",
        "Interrompere i costanti *cicli di ritorno*",
        "Recuperare *lucidità e pace mentale*"
      ]
    },
    {
      num: "02",
      title: "Fase 2 — Ritorno a Sé",
      subtitle: "Ricostruire le tue fondamenta invisibili",
      description: "Questo è il cuore della trasformazione. Spostiamo l'attenzione dall'altro a te stessa. Ricostruiamo la tua autostima, la tua identità e stabiliamo confini sani e invalicabili.",
      highlights: [
        "Ricostruire l'*autostima* e l'*identità autonoma*",
        "Sviluppare una reale *sicurezza emotiva*",
        "Imparare a comunicare e difendere i propri *confini*"
      ]
    },
    {
      num: "03",
      title: "Fase 3 — Nuovi Standard",
      subtitle: "Imparare a scegliere relazioni sane",
      description: "Imparerai a riconoscere le red flag istantaneamente, senza giustificarle. Non si tratta di non amare più, ma di non accettare più l'amore che ti distrugge.",
      highlights: [
        "Riconoscere istantaneamente le *red flag*",
        "Scegliere consapevolmente *relazioni e partner sani*",
        "Costruire un nuovo, solido *standard di vita sentimentale*"
      ]
    }
  ];

  const renderKeywords = (text) =>
    text.split(/(\*[^*]+\*)/g).map((part, i) =>
      part.startsWith("*") && part.endsWith("*") ? (
        <span key={i} className="text-champagne font-medium">{part.slice(1, -1)}</span>
      ) : (
        part
      )
    );

  return (
    <section id="metodo" className="py-24 md:py-32 bg-beige">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        
        {/* Left column: Sticky description and image */}
        <div className="lg:col-span-5 lg:sticky lg:top-32 space-y-8">
          <div className="space-y-4">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-bordeaux">
              IL METODO PROPRIETARIO
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-chocolate font-editorial leading-tight">
              Il Metodo <br />
              <span className="italic text-champagne-dark">Feminine Return™</span>
            </h2>
          </div>
          
          <p className="text-base leading-relaxed text-chocolate/80 font-light">
            Un percorso individuale esclusivo di <span className="text-champagne font-medium">60 giorni</span>, strutturato per accompagnarti passo dopo passo fuori dalla nebbia emotiva, fino alla totale consapevolezza del tuo valore.
          </p>

          <div className="relative aspect-[4/3] border border-chocolate/10 overflow-hidden mt-6">
            <img
              src={metodoImg}
              alt="Il Metodo Feminine Return™"
              className="w-full h-full object-cover grayscale-[10%]"
            />
          </div>
        </div>

        {/* Right column: Interactive Phase Steps */}
        <div className="lg:col-span-7 space-y-8">
          {phases.map((phase, idx) => (
            <div
              key={idx}
              onClick={() => setActivePhase(idx)}
              className={`p-8 border bg-bordeaux cursor-pointer transition-luxury ${
                activePhase === idx ? "border-champagne/30 border-l-4 border-l-champagne shadow-sm" : "border-champagne/20 hover:border-champagne/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold tracking-widest text-champagne">
                  {phase.num}
                </span>
                <span className="text-xs font-mono text-ivory/50">FASE</span>
              </div>

              <div className="mt-4 space-y-4">
                <h3 className="text-xl sm:text-2xl font-normal tracking-tight font-editorial text-ivory">
                  {phase.title.split("—")[0]}—
                  <span className="text-champagne">{phase.title.split("—")[1]}</span>
                </h3>

                {activePhase === idx && (
                  <div className="space-y-6 pt-2 animate-fade-in">
                    <p className="text-sm sm:text-base leading-relaxed text-ivory/90 font-light">
                      {phase.description}
                    </p>
                    <ul className="space-y-3">
                      {phase.highlights.map((h, hIdx) => (
                        <li key={hIdx} className="flex items-start space-x-3 text-sm text-ivory/85">
                          <CheckCircle2 className="w-4 h-4 text-champagne shrink-0 mt-1" />
                          <span>{renderKeywords(h)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
